import React from 'react';
import { type DataSchemaProperty, type DataType, type FieldDefinition } from '@/types';
import { FIELD_TYPE_RENDERERS, type FieldTypeRenderer, FIELD_OVERRIDES } from './fieldRenderers';
import { resolveIcon } from './iconMapping';
import { resolveRenderFunction } from './renderFunctions';

/** Whether people may edit this field through the UI/API. Server code and channel rules may still write 'system' fields. */
export function isFieldEditableByHumans(fieldDef: Pick<FieldDefinition, 'mutability'>): boolean {
	return fieldDef.mutability !== 'system' && fieldDef.mutability !== 'locked';
}

export function convertSchemaPropertyToFieldDefinition(
	key: string,
	prop: DataSchemaProperty,
	renderers: Record<DataType, FieldTypeRenderer> = FIELD_TYPE_RENDERERS,
	overrides: Record<string, Partial<FieldDefinition>> = FIELD_OVERRIDES
): FieldDefinition {
	const fieldType = prop['x-field-type']!;

	const typeRenderer = renderers[fieldType] || renderers.text;

	const baseDefinition: FieldDefinition = {
		key,
		label: prop.title || key,
		description: prop.description,
		type: fieldType,
		searchable: prop['x-searchable'] ?? true,
		displayable: prop['x-displayable'] ?? true,
		placeholder: typeRenderer.placeholder,
		'x-reserved': prop['x-reserved']
	};

	if (prop['x-icon-name']) {
		const IconComponent = resolveIcon(prop['x-icon-name']);
		baseDefinition.icon = <IconComponent className="mr-2 h-4 w-4" />;
	} else {
		const IconComponent = typeRenderer.icon;
		baseDefinition.icon = <IconComponent className="mr-2 h-4 w-4" />;
	}

	if (prop['x-render-type']) {
		baseDefinition.renderType = prop['x-render-type'];
		baseDefinition.render = resolveRenderFunction(prop['x-render-type']);
	} else {
		baseDefinition.render = typeRenderer.render;
	}

	if (prop['x-mutability']) {
		baseDefinition.mutability = prop['x-mutability'];
	}

	if (prop['x-visibility']) {
		baseDefinition.visibility = prop['x-visibility'];
	}

	if (fieldType === 'select' && prop.enum) {
		baseDefinition.options = prop.enum.map(value => ({
			value,
			label: value
		}));
	}

	// Populated dynamically at runtime in getFieldDefinitions()
	if (fieldType === 'systemUserSelect') {
		baseDefinition.options = [];
	}

	if (prop['x-ai-model']) {
		baseDefinition.promptModel = prop['x-ai-model'];
	}
	if (prop['x-ai-prompt']) {
		baseDefinition.promptText = prop['x-ai-prompt'];
	}

	const override = overrides[key];
	if (override) {
		return { ...baseDefinition, ...override };
	}

	return baseDefinition;
}
